import { useEffect, useState } from "react";
import { FiLoader } from "react-icons/fi";
import DialogModal from "../dialog-modal/dialog-modal.component";
import FormError from "../form-error/form-error.component";
import SelectBox from "../form-inputs/select-box.component";
import { Button } from "../ui/button";
import { cn } from "../../lib/utils";
import strings from "../../global/strings";
import { fromAdToBs, getNepaliFiscalYears } from "../../global/function";
import type { SalesBookCalendar, SalesBookDuration } from "../../queries/bill.queries";

type DurationTab = "month" | "range" | "fiscal_year";

const DURATION_TABS: Array<{ id: DurationTab; label: string }> = [
	{ id: "month", label: "Single Month" },
	{ id: "range", label: "Range" },
	{ id: "fiscal_year", label: "Fiscal Year" },
];

const [currentNepYearStr, currentNepMonthStr] = fromAdToBs(new Date(), "YYYY-MM-DD").split("-");
const currentNepYear = +currentNepYearStr || new Date().getFullYear();
const currentNepMonth = +currentNepMonthStr || 1;
const currentEnYear = new Date().getFullYear();
const currentEnMonth = new Date().getMonth() + 1;

// getNepYear() interleaves plain-year and fiscal-year-keyed entries — only the
// plain numeric ones (id is a number) are years for a month/year picker.
const nepaliYearOptions = strings.nepaliYear.filter((y) => typeof y.id === "number");
const fiscalYearOptions = getNepaliFiscalYears();

interface SalesBookOptionsDialogProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	isPending: boolean;
	isError?: boolean;
	error?: unknown;
	onConfirm: (duration: SalesBookDuration, useEnDate: boolean) => void;
}

export default function SalesBookOptionsDialog({
	open,
	onOpenChange,
	isPending,
	isError,
	error,
	onConfirm,
}: SalesBookOptionsDialogProps) {
	const [tab, setTab] = useState<DurationTab>("month");
	const [calendar, setCalendar] = useState<SalesBookCalendar>("np");

	const [year, setYear] = useState(currentNepYear);
	const [month, setMonth] = useState(currentNepMonth);
	const [fromYear, setFromYear] = useState(currentNepYear);
	const [fromMonth, setFromMonth] = useState(currentNepMonth);
	const [toYear, setToYear] = useState(currentNepYear);
	const [toMonth, setToMonth] = useState(currentNepMonth);
	const [fiscalYear, setFiscalYear] = useState("");

	// Reset to sensible defaults every time the dialog reopens, same reasoning
	// as PrintOptionsDialog's own resync-on-open effect.
	useEffect(() => {
		if (!open) return;
		setTab("month");
		setCalendar("np");
		setYear(currentNepYear);
		setMonth(currentNepMonth);
		setFromYear(currentNepYear);
		setFromMonth(currentNepMonth);
		setToYear(currentNepYear);
		setToMonth(currentNepMonth);
		setFiscalYear("");
	}, [open]);

	// Switching calendar resets year/month to "now" in that calendar — a
	// previously-picked NP year/month has no sane EN equivalent to carry over.
	function handleCalendarChange(next: SalesBookCalendar) {
		setCalendar(next);
		const y = next === "np" ? currentNepYear : currentEnYear;
		const m = next === "np" ? currentNepMonth : currentEnMonth;
		setYear(y);
		setMonth(m);
		setFromYear(y);
		setFromMonth(m);
		setToYear(y);
		setToMonth(m);
	}

	const monthOptions = calendar === "np" ? strings.nepaliMonth : strings.englishMonth;
	const yearOptions = calendar === "np" ? nepaliYearOptions : strings.englishYear;

	function handleConfirm() {
		if (tab === "fiscal_year") {
			if (!fiscalYear) return;
			onConfirm({ duration_type: "fiscal_year", fiscal_year: fiscalYear }, calendar === "en");
		} else if (tab === "month") {
			onConfirm({ duration_type: "month", calendar, year, month }, calendar === "en");
		} else {
			onConfirm(
				{
					duration_type: "range",
					calendar,
					from_year: fromYear,
					from_month: fromMonth,
					to_year: toYear,
					to_month: toMonth,
				},
				calendar === "en",
			);
		}
	}

	return (
		<DialogModal
			open={open}
			onOpenChange={onOpenChange}
			title="Generate sales book"
			description="Choose the duration of sales to include."
			footer={
				<>
					<Button variant="outline" onClick={() => onOpenChange(false)} disabled={isPending}>
						Cancel
					</Button>
					<Button
						onClick={handleConfirm}
						disabled={isPending || (tab === "fiscal_year" && !fiscalYear)}
					>
						{isPending ? (
							<>
								<FiLoader className="animate-spin" /> Generating...
							</>
						) : (
							"Generate"
						)}
					</Button>
				</>
			}
		>
			<div className="grid grid-cols-3 gap-2">
				{DURATION_TABS.map((option) => (
					<button
						key={option.id}
						type="button"
						onClick={() => setTab(option.id)}
						disabled={isPending}
						className={cn(
							"rounded-md border px-3 py-2 text-sm transition-colors disabled:pointer-events-none disabled:opacity-50",
							tab === option.id
								? "border-primary bg-primary/10 text-foreground"
								: "border-border bg-background hover:bg-muted",
						)}
					>
						{option.label}
					</button>
				))}
			</div>

			{tab !== "fiscal_year" && (
				<div className="mt-3 flex gap-2">
					{(["np", "en"] as const).map((option) => (
						<button
							key={option}
							type="button"
							onClick={() => handleCalendarChange(option)}
							disabled={isPending}
							className={cn(
								"rounded-md border px-3 py-1.5 text-sm transition-colors disabled:pointer-events-none disabled:opacity-50",
								calendar === option
									? "border-primary bg-primary/10 text-foreground"
									: "border-border bg-background hover:bg-muted",
							)}
						>
							{option === "np" ? "Nepali Calendar" : "English Calendar"}
						</button>
					))}
				</div>
			)}

			{tab === "month" && (
				<div className="mt-3 flex gap-2">
					<SelectBox
						options={monthOptions}
						value={month}
						onChange={(e) => setMonth(+e.target.value)}
						disabled={isPending}
					/>
					<SelectBox
						options={yearOptions}
						value={year}
						onChange={(e) => setYear(+e.target.value)}
						disabled={isPending}
					/>
				</div>
			)}

			{tab === "range" && (
				<div className="mt-3 flex flex-col gap-2">
					<div className="flex items-center gap-2">
						<span className="w-12 text-sm text-muted-foreground">From</span>
						<SelectBox
							options={monthOptions}
							value={fromMonth}
							onChange={(e) => setFromMonth(+e.target.value)}
							disabled={isPending}
						/>
						<SelectBox
							options={yearOptions}
							value={fromYear}
							onChange={(e) => setFromYear(+e.target.value)}
							disabled={isPending}
						/>
					</div>
					<div className="flex items-center gap-2">
						<span className="w-12 text-sm text-muted-foreground">To</span>
						<SelectBox
							options={monthOptions}
							value={toMonth}
							onChange={(e) => setToMonth(+e.target.value)}
							disabled={isPending}
						/>
						<SelectBox
							options={yearOptions}
							value={toYear}
							onChange={(e) => setToYear(+e.target.value)}
							disabled={isPending}
						/>
					</div>
				</div>
			)}

			{tab === "fiscal_year" && (
				<div className="mt-3">
					<SelectBox
						options={fiscalYearOptions}
						value={fiscalYear}
						onChange={(e) => setFiscalYear(e.target.value)}
						disabled={isPending}
					/>
				</div>
			)}

			<FormError isError={isError} error={error} fallback="Failed to generate sales book. Please try again." />
		</DialogModal>
	);
}
